import React, { useState, useEffect } from 'react';
import { Card, CardContent, Box, CardActionArea, Typography } from '@mui/material';
import Pagination from '@mui/material/Pagination';
import useMediaQuery from '@mui/material/useMediaQuery';
import dayjs from 'dayjs';
import { useTranslation, Trans } from 'react-i18next';
import EventCard from '../EventCard';
import Footer from '../Footer';
import PortalFilters from './PortalFilters';
import { getPortalClasses } from './portalStyles';
import { ACCOUNT_ID, PAGE_SIZE, CUSTOM_FIELDS } from './portalConfigs';
import { fetchFromConnect } from '../../api/connect';

export const buildCalendarPath = (config, filters, page) => {
	const params = new URLSearchParams();

	// No selection in Home = all its types, which keeps FEAST out.
	const eventType = config.fixedEventType
		|| filters.event_type
		|| (config.eventTypeOptions || []).join(',');
	params.append(`search_custom_fields[${CUSTOM_FIELDS.eventType}]`, eventType);

	if (filters.start_date) {
		params.append('start_date', dayjs(filters.start_date).format('YYYY-MM-DD'));
	}
	if (filters.end_date) {
		params.append('end_date', dayjs(filters.end_date).format('YYYY-MM-DD'));
	}

	const location = Array.isArray(filters.location)
		? filters.location.join(',')
		: filters.location;
	if (location) params.append(`search_custom_fields[${config.locationFieldId || CUSTOM_FIELDS.location}]`, location);

	(config.extraFilters || []).forEach(({ filterKey, fieldId }) => {
		if (filters[filterKey]) params.append(`search_custom_fields[${fieldId}]`, filters[filterKey]);
	});

	// Only one language goes to the API; several are filtered in loadEvents.
	if (filters.language.length === 1) {
		params.append(`search_custom_fields[${CUSTOM_FIELDS.language}]`, filters.language[0]);
	}

	return `/accounts/${ACCOUNT_ID}/calendar?page[size]=${PAGE_SIZE}&page[number]=${page}`
		+ `&show_in_widget=true&execution_status=current_and_upcoming&${params.toString()}`;
};

const EventsPortal = ({ config }) => {
	const [events, setEvents] = useState([]);
	const [loadError, setLoadError] = useState(false);
	const [totalEvents, setTotalEvents] = useState(0);
	const [totalPages, setTotalPages] = useState(0);
	const [page, setPage] = useState(1);
	const [filterValues, setFilterValues] = useState(config.defaultFilters);
	// Type of the last search (not the select): drives the title and "no events" message.
	const [searchedEventType, setSearchedEventType] = useState(config.defaultFilters.event_type);
	const isMobile = useMediaQuery('(max-width:768px)');
	const { t } = useTranslation();
	const classes = getPortalClasses(config.layout);

	const title = config.layout === 'home'
		? t(`eventTypeTitles.${searchedEventType}`)
		: t(config.texts.title);

	const loadEvents = (pageNumber) => {
		setLoadError(false);
		fetchFromConnect(buildCalendarPath(config, filterValues, pageNumber))
			.then((data) => {
				const pageEvents = data.data || [];
				const { language, location } = filterValues;
				const noLocationSelected = config.multipleLocations && location.length === 0;

				if (language.length === 0 || noLocationSelected) {
					setEvents([]);
				} else if (language.length > 1) {
					setEvents(pageEvents.filter(
						event => language.includes(event.attributes.fields?.[CUSTOM_FIELDS.language])));
				} else {
					setEvents(pageEvents);
				}

				setTotalEvents(data.meta.total_events);
				setTotalPages(data.meta.total_pages);
			})
			// Without this the rejection went unhandled and the list stayed empty, so an
			// API or CORS failure was indistinguishable from a portal with no events.
			.catch((error) => {
				console.error(`${config.name}: could not load the event list`, error);
				setLoadError(true);
			});
	};

	useEffect(() => {
		if (filterValues === config.defaultFilters) {
			setPage(1);
			loadEvents(1);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterValues]);

	const handlePageChange = (event, pageNumber) => {
		setPage(pageNumber);
		loadEvents(pageNumber);
	};

	const searchEvents = () => {
		setPage(1);
		loadEvents(1);
		setSearchedEventType(filterValues.event_type);
	};

	const clearFilters = () => {
		setFilterValues(config.defaultFilters);
		setSearchedEventType(config.defaultFilters.event_type);
	};

	const renderNoEvents = () => {
		const custom = config.noEventsByType?.[searchedEventType];
		return (
			<Typography sx={classes.noEvents}>
				{custom ? (
					<Trans
						t={t}
						i18nKey={custom.i18nKey}
						components={{
							1: (
								// eslint-disable-next-line jsx-a11y/anchor-has-content
								<a href={custom.link} target="_blank" rel="noopener noreferrer" />
							),
						}}
					/>
				) : t('noEvents')}
			</Typography>
		);
	};

	return (
		<Box sx={{
			minHeight: '100dvh',
			display: 'flex',
			flexDirection: 'column',
		}}>
			<Box sx={{ flex: 1 }}>
				<Card sx={classes.eventsCard}>
					<PortalFilters
						config={config}
						classes={classes}
						isMobile={isMobile}
						title={title}
						filterValues={filterValues}
						setFilterValues={setFilterValues}
						onSearch={searchEvents}
						onClear={clearFilters}
					/>
					{loadError ? (
						<Typography sx={classes.noEvents}>
							{t('loadError')}
						</Typography>
					) : events.length === 0 ? renderNoEvents() : (
						<CardContent style={{ padding: '24px 26px' }}>
							<Box sx={classes.cardsContainer}>
								{events.map((item, key) => (
									<CardActionArea
										key={item?.id || key}
										component="a"
										href={item?.attributes?.website ? item?.attributes?.website : `https://live.eventtia.com/en/${item?.attributes?.event_uri}`}
										target="_blank"
										rel="noopener noreferrer"
									>
										<EventCard event={item} hideEventDates={config.hideEventDates} />
									</CardActionArea>
								))}
							</Box>
							{totalPages > 1 && (
								<Box sx={classes.pagination}>
									<Pagination
										page={page}
										count={Math.ceil(totalEvents / PAGE_SIZE)}
										onChange={handlePageChange}
									/>
								</Box>
							)}
						</CardContent>
					)}
				</Card>
			</Box>
			<Footer contactEmail={config.contactEmail} />
		</Box>
	);
};

export default EventsPortal;
